// redux/slices/takbullConnect.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

/* ===================== HELPERS ===================== */

const getError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const handleAuthError = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch { }
  }
};

const getUid = (getState, providedUid) => {
  const state = getState();

  const uid =
    providedUid ||
    state?.master?.master?.uid ||
    state?.master?.uid ||
    state?.auth?.uid ||
    null;

  if (!uid) throw new Error("UID required");

  return uid;
};

const normalizeTakbullResponse = (payload = {}) => {
  const root = payload || {};

  const data =
    root?.data ||
    root?.result ||
    root?.credentials ||
    root?.credential ||
    root;

  const credentials =
    data?.credentials ||
    data?.credential ||
    data;

  const connected = Boolean(
    root?.connected ??
    root?.isConnected ??
    root?.hasCredentials ??
    root?.exists ??
    data?.connected ??
    data?.isConnected ??
    data?.hasCredentials ??
    data?.exists ??
    credentials?.connected ??
    credentials?.isConnected ??
    credentials?.hasCredentials ??
    credentials?.exists ??
    credentials?.keyMask ??
    credentials?.apiKeyMask
  );

  const keyMask =
    root?.keyMask ??
    root?.apiKeyMask ??
    data?.keyMask ??
    data?.apiKeyMask ??
    credentials?.keyMask ??
    credentials?.apiKeyMask ??
    null;

  const updatedAt =
    root?.updatedAt ??
    data?.updatedAt ??
    credentials?.updatedAt ??
    credentials?.updated_at ??
    null;

  const defaults =
    root?.defaults ??
    data?.defaults ??
    credentials?.defaults ??
    {
      taxRate:
        root?.taxRate ??
        root?.vatRate ??
        data?.taxRate ??
        data?.vatRate ??
        credentials?.taxRate ??
        credentials?.vatRate ??
        null,

      vatCode:
        root?.vatCode ??
        data?.vatCode ??
        credentials?.vatCode ??
        null,
    };

  return {
    connected,
    keyMask,
    updatedAt,
    defaults,
  };
};

const applyTakbullStatus = (state, payload = {}) => {
  const normalized = normalizeTakbullResponse(payload);

  state.connected = normalized.connected;
  state.keyMask = normalized.keyMask;
  state.updatedAt = normalized.updatedAt;
  state.defaults = normalized.defaults;
};
/* ===================== THUNKS ===================== */

export const saveTakbullCreds = createAsyncThunk(
  "takbullConnect/saveCreds",
  async (
    { uid: uidArg, apiKey, apiSecret, defaults } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const uid = getUid(getState, uidArg);

      const res = await apiRequest("POST", "/takbull/credentials", {
        uid,
        apiKey,
        apiSecret,
        defaults,
      });

      return res?.data || res;
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Save Takbull credentials failed"));
    }
  }
);

export const fetchTakbullStatus = createAsyncThunk(
  "takbullConnect/fetchStatus",
  async (uidArg, { getState, rejectWithValue }) => {
    try {
      const uid = getUid(getState, uidArg);

      const res = await apiRequest(
        "GET",
        `/takbull/credentials/${encodeURIComponent(uid)}`
      );

      const response = res?.data || res;

      return response;
    } catch (error) {
      await handleAuthError(error);

      console.error("❌ [fetchTakbullStatus error]", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      return rejectWithValue(
        getError(error, "Fetch Takbull status failed")
      );
    }
  }
);

export const disconnectTakbull = createAsyncThunk(
  "takbullConnect/disconnect",
  async (uidArg, { getState, rejectWithValue }) => {
    try {
      const uid = getUid(getState, uidArg);

      const res = await apiRequest(
        "DELETE",
        `/takbull/credentials/${encodeURIComponent(uid)}`
      );

      return res?.data || res || { ok: true };
    } catch (error) {
      await handleAuthError(error);
      return rejectWithValue(getError(error, "Disconnect Takbull failed"));
    }
  }
);

export const connectTakbull = createAsyncThunk(
  "takbullConnect/connect",
  async (
    { uid: uidArg, apiKey, apiSecret, defaults } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const uid = getUid(getState, uidArg);

      const res = await apiRequest("POST", "/takbull/credentials", {
        uid,
        apiKey,
        apiSecret,
        defaults,
      });

      const response = res?.data || res;


      return response;
    } catch (error) {
      await handleAuthError(error);

      console.error("❌ [connectTakbull error]", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });

      return rejectWithValue(
        getError(error, "Connect Takbull failed")
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  connected: false,
  keyMask: null,
  updatedAt: null,
  defaults: null,

  saving: false,
  status: "idle",
  error: null,
};

/* ===================== SLICE ===================== */

const takbullConnectSlice = createSlice({
  name: "takbullConnect",
  initialState,
  reducers: {
    clearTakbullError: (state) => {
      state.error = null;
    },
    resetTakbullStatus: (state) => {
      state.status = "idle";
      state.saving = false;
      state.error = null;
    },
    clearTakbull: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveTakbullCreds.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveTakbullCreds.fulfilled, (state, action) => {
        state.saving = false;
        state.status = "succeeded";
        state.error = null;

        applyTakbullStatus(state, action.payload);


      })
      .addCase(saveTakbullCreds.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Save credentials failed";
      })

      .addCase(connectTakbull.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(connectTakbull.fulfilled, (state, action) => {
        state.saving = false;
        state.status = "succeeded";
        state.error = null;

        applyTakbullStatus(state, action.payload);

      })
      .addCase(connectTakbull.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Connect failed";
      })

      .addCase(fetchTakbullStatus.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTakbullStatus.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;

        applyTakbullStatus(state, action.payload);
      })
      .addCase(fetchTakbullStatus.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Status failed";
      })

      .addCase(disconnectTakbull.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(disconnectTakbull.fulfilled, (state) => {
        state.saving = false;
        state.status = "succeeded";
        state.error = null;
        state.connected = false;
        state.keyMask = null;
        state.updatedAt = null;
        state.defaults = null;
      })
      .addCase(disconnectTakbull.rejected, (state, action) => {
        state.saving = false;
        state.error =
          action.payload ||
          action.error?.message ||
          "Disconnect failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearTakbullError,
  resetTakbullStatus,
  clearTakbull,
} = takbullConnectSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectTakbullConnect = (state) =>
  state?.takbullConnect || initialState;

export const selectTakbull = selectTakbullConnect;

export const selectTakbullConnected = (state) =>
  Boolean(selectTakbullConnect(state).connected);

export const selectTakbullStatus = (state) =>
  selectTakbullConnect(state).status || "idle";

export const selectTakbullSaving = (state) =>
  Boolean(selectTakbullConnect(state).saving);

export const selectTakbullError = (state) =>
  selectTakbullConnect(state).error || null;

export const selectTakbullDefaults = (state) =>
  selectTakbullConnect(state).defaults || null;

export const selectTakbullKeyMask = (state) =>
  selectTakbullConnect(state).keyMask || null;

export const selectTakbullUpdatedAt = (state) =>
  selectTakbullConnect(state).updatedAt || null;

export default takbullConnectSlice.reducer;
